import { useEffect, useRef } from 'react';
import { showStatusToast } from './statusToast';

const FLASH_TYPES = new Set(['error', 'success', 'warning', 'info', 'loading']);

/**
 * Shared status feedback. Flash messages are toast-only (never an inline banner).
 * Pass `setStatus` so the message is cleared after toasting (prevents any leftover banner UI).
 * Loading toasts stay sticky and are updated in place until success/error/ready.
 * Optional `action` still renders a compact retry control for recoverable load errors.
 */
export function StatusBanner({ status, setStatus = null, className = '', action = null }) {
  const lastKeyRef = useRef('');

  useEffect(() => {
    if (!status?.message || !FLASH_TYPES.has(status.type)) {
      // After a loading toast is shown we clear its message but keep type: 'loading'.
      // Do not dismiss the sticky toast or reset lastKey — that caused a toast loop
      // while background sync polls updated status every ~1.5s.
      if (status?.type === 'loading') {
        return;
      }

      // ready/idle with no message: leave lastKey alone if we just finished a flash
      // (success/error handlers already dismissed loading). Avoid extra dismiss spam.
      if (status?.type === 'ready' || status?.type === 'idle' || !status?.type) {
        return;
      }

      lastKeyRef.current = '';
      return;
    }

    const key = `${status.type}::${status.message}`;
    if (key === lastKeyRef.current) {
      return;
    }
    lastKeyRef.current = key;

    const type = status.type;
    const message = status.message;
    showStatusToast({ type, message });

    if (typeof setStatus === 'function') {
      // Clear message so nothing can render it as an inline banner.
      // Keep type: 'loading' so the sticky toast is not dismissed on the next effect.
      queueMicrotask(() => {
        setStatus((current) => {
          if (!current || current.message !== message || current.type !== type) {
            return current;
          }
          if (type === 'loading') {
            return { type: 'loading', message: '' };
          }
          if (type === 'error' && action) {
            // Keep error type so retry action strip can show; message lives in the toast.
            return { type: 'error', message: '' };
          }
          return { type: 'ready', message: '' };
        });
      });
    }
  }, [status?.type, status?.message, setStatus, action]);

  if (action && status?.type === 'error') {
    return (
      <div
        className={`flex flex-wrap items-center justify-end gap-3 ${className}`.trim()}
        role="status"
      >
        <div className="shrink-0">{action}</div>
      </div>
    );
  }

  return null;
}

/** First-load placeholder when a table has no rows yet. */
export function PageLoading({ label = 'Loading…' }) {
  return (
    <p className="rounded-md border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
      {label}
    </p>
  );
}
